import type { Ref } from 'vue';
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatCurrency } from '@/utils/formatUtils';
import {
    normalizePaymentsChartRange,
    rangeIntersectsCurrentChartPeriod,
    chartBucketRange,
    chartBucketKind,
} from '@/utils/chartRangeNormalizer';
import { externalTooltip, bucketTitle } from '@/utils/chartTooltip';
import { formatChartLabel } from '@/utils/dt';
import PAYMENT_CHART_COLORS from '@/utils/paymentChartColors';
import { useTimezoneStore } from '@/stores/timezone';
import {
    getPaymentsReportChart,
    getPaymentsReportStats,
    type PaymentsReportChart,
    type PaymentsReportMetricKey,
    type PaymentsReportStats,
} from '@/services/reportsService';
import { PaymentStatus, type PaymentStatusValue } from '@/services/paymentStatus';

export interface ReportStatItem {
    id: string;
    label: string;
    value: string | number;
    icon: string;
}

type ViewMode = 'days' | 'weeks' | 'months';

interface CategoryEntry {
    labels: string[];
    values: number[];
}

type CategoryMap = Record<string, CategoryEntry>;

const DATASET_BASE = Object.freeze({
    backgroundColor: PAYMENT_CHART_COLORS.cleared,
    borderRadius: { topLeft: 12, topRight: 12 },
    borderSkipped: 'bottom' as const,
    maxBarThickness: 80,
});

const SERIES_LABEL: Record<PaymentsReportMetricKey, string> = {
    cleared: 'Cleared',
    upcoming: 'Upcoming / Pending',
    'low-balance': 'Low Balance',
    canceled: 'Canceled',
};

const METRIC_TARGET: Record<PaymentsReportMetricKey, { tab: string; status?: PaymentStatusValue[] }> = {
    cleared: { tab: 'deposits' },
    upcoming: { tab: 'other', status: [PaymentStatus.OPEN, PaymentStatus.PENDING] },
    'low-balance': { tab: 'other', status: [PaymentStatus.LOW_BALANCE] },
    canceled: { tab: 'other', status: [PaymentStatus.CANCEL] },
};

const STAT_DEFINITIONS: Array<{ id: PaymentsReportMetricKey; label: string; icon: string }> = [
    { id: 'cleared', label: 'Total Cleared', icon: 'fa-solid fa-money-bill-transfer' },
    { id: 'upcoming', label: 'Total Upcoming / Pending', icon: 'fa-solid fa-money-bill-trend-up' },
    { id: 'low-balance', label: 'Total Low Balance', icon: 'fa-solid fa-scale-unbalanced' },
    { id: 'canceled', label: 'Total Canceled', icon: 'fa-solid fa-dollar-sign' },
];

function emptyCategoryData(): CategoryMap {
    return {
        cleared: { labels: [], values: [] },
        upcoming: { labels: [], values: [] },
        'low-balance': { labels: [], values: [] },
        canceled: { labels: [], values: [] },
    };
}

function emptyDailyAverages(): Record<PaymentsReportMetricKey, number> {
    return { cleared: 0, upcoming: 0, 'low-balance': 0, canceled: 0 };
}

function formatLabelsForView(labels: string[], mode: ViewMode): string[] {
    if (mode !== 'months') return labels;
    return labels.map((label) => {
        const slashMatch = label.match(/^(\d{1,2})\/(\d{4})$/);
        if (slashMatch) {
            const [, month, year] = slashMatch;
            return formatChartLabel(`${year}-${month.padStart(2, '0')}-01`, mode);
        }
        const dashMatch = label.match(/^(\d{4})-(\d{1,2})$/);
        if (dashMatch) {
            const [, year, month] = dashMatch;
            return formatChartLabel(`${year}-${month.padStart(2, '0')}-01`, mode);
        }
        return label;
    });
}

function mapChartToCategoryData(chart: PaymentsReportChart, mode: ViewMode): CategoryMap {
    const labels = formatLabelsForView(chart.labels, mode);
    return {
        cleared: { labels, values: chart.series.cleared },
        upcoming: { labels, values: chart.series.upcoming },
        'low-balance': { labels, values: chart.series['low-balance'] },
        canceled: { labels, values: chart.series.canceled },
    };
}

function sumValues(values: number[]): number {
    return values.reduce((a, b) => a + b, 0);
}

export function useReportsPayments(dateRange: Ref<[string, string] | null>) {
    const viewMode = ref<ViewMode>('days');
    const activeStatId = ref<string | null>(null);
    const tzStore = useTimezoneStore();
    const router = useRouter();

    const chartRange = ref<{ from: string; to: string }>({ from: '', to: '' });
    const loading = ref(true);
    const categoryData = ref<CategoryMap>(emptyCategoryData());
    const dailyAverages = ref<Record<PaymentsReportMetricKey, number>>(emptyDailyAverages());
    const reportStats = ref<PaymentsReportStats>({ cleared: 0, upcoming: 0, 'low-balance': 0, canceled: 0 });

    const stats = computed<ReportStatItem[]>(() =>
        STAT_DEFINITIONS.map((def) => ({
            ...def,
            value: formatCurrency(reportStats.value[def.id as PaymentsReportMetricKey] ?? 0),
        })),
    );

    const seriesBackgroundColor = (
        metric: PaymentsReportMetricKey,
        values: number[],
    ): string | string[] => {
        if (metric !== 'upcoming') return PAYMENT_CHART_COLORS.cleared;

        const { from, to } = chartRange.value;
        if (!from || !to) return PAYMENT_CHART_COLORS.cleared;

        return values.map((_, index) => {
            const bucket = chartBucketRange(from, to, viewMode.value, index);
            const kind = chartBucketKind(bucket, tzStore.effectiveTimezone);

            return kind === 'upcoming'
                ? PAYMENT_CHART_COLORS.upcoming
                : PAYMENT_CHART_COLORS.cleared;
        });
    };

    const chartData = computed(() => {
        const cats = categoryData.value;
        const labels = cats.cleared.labels;
        const activeId = activeStatId.value;

        if (activeId && cats[activeId]) {
            const metric = activeId as PaymentsReportMetricKey;
            return {
                labels,
                datasets: [{
                    ...DATASET_BASE,
                    backgroundColor: seriesBackgroundColor(metric, cats[activeId].values),
                    data: cats[activeId].values,
                }],
            };
        }

        return {
            labels,
            datasets: STAT_DEFINITIONS.map((def) => ({
                ...DATASET_BASE,
                backgroundColor: seriesBackgroundColor(
                    def.id,
                    cats[def.id]?.values ?? [],
                ),
                data: cats[def.id]?.values ?? [],
            })),
        };
    });

    const dailyAverage = computed(() => {
        const activeId = activeStatId.value;
        if (activeId && dailyAverages.value[activeId as PaymentsReportMetricKey] !== undefined) {
            return formatCurrency(dailyAverages.value[activeId as PaymentsReportMetricKey]);
        }
        const total = STAT_DEFINITIONS.reduce((sum, def) => sum + (dailyAverages.value[def.id] ?? 0), 0);
        return formatCurrency(total);
    });

    const bucketAt = (index: number) => {
        const { from, to } = chartRange.value;
        if (!from || !to) return null;
        return chartBucketRange(from, to, viewMode.value, index);
    };

    /**
     * Which series was clicked decides the target tab: the selected stat card when one is
     * active, otherwise the bar's own dataset (BarChart blocks off-bar clicks on grouped charts).
     */
    const openBucket = (index: number, datasetIndex: number | null) => {
        const bucket = bucketAt(index);
        if (!bucket) return;

        const metric = (activeStatId.value as PaymentsReportMetricKey | null)
            ?? (datasetIndex !== null ? STAT_DEFINITIONS[datasetIndex]?.id : undefined);
        if (!metric) return;

        const target = METRIC_TARGET[metric];
        router.push({
            path: '/payments',
            query: {
                tab: target.tab,
                ...(target.status ? { status: target.status } : {}),
                from: bucket.from,
                to: bucket.to,
            },
        });
    };

    const chartOptions = computed(() => ({
        interaction: { mode: 'index' as const, intersect: true },

        plugins: {
            legend: { display: false },
            tooltip: externalTooltip({
                title: (items: Array<{ dataIndex: number; label: string }>) => {
                    const item = items[0];
                    if (!item) return '';
                    return bucketTitle(item.label, viewMode.value, bucketAt(item.dataIndex));
                },
                label: (ctx: { datasetIndex: number; parsed: { y: number } }) => {
                    const key = (activeStatId.value as PaymentsReportMetricKey | null)
                        ?? STAT_DEFINITIONS[ctx.datasetIndex]?.id;
                    const name = key ? SERIES_LABEL[key] : 'Payments';
                    return `${name}: ${formatCurrency(ctx.parsed.y ?? 0)}`;
                },
            }),
        },
    }));

    const rangeIncludesCurrentPeriod = computed(() => {
        const range = dateRange.value;
        if (!range) return false;
        const [start, end] = range;
        return rangeIntersectsCurrentChartPeriod(start, end, viewMode.value, tzStore.effectiveTimezone);
    });

    const fetchData = async (onCleanup: (cleanupFn: () => void) => void) => {
        const range = dateRange.value;
        if (!range) {
            loading.value = true;
            return;
        }

        let isCurrent = true;
        onCleanup(() => { isCurrent = false; });

        loading.value = true;
        const [startDate, endDate] = range;
        const currentViewMode = viewMode.value;
        const timezone = tzStore.effectiveTimezone;
        const normalized = normalizePaymentsChartRange(startDate, endDate, currentViewMode, 20, timezone);

        try {
            const [statsResponse, chartResponse] = await Promise.all([
                getPaymentsReportStats(startDate, endDate),
                getPaymentsReportChart(normalized.from, normalized.to, currentViewMode),
            ]);
            if (!isCurrent) return;
            chartRange.value = normalized;
            reportStats.value = statsResponse;
            dailyAverages.value = chartResponse.dailyAverages;
            categoryData.value = mapChartToCategoryData(chartResponse, currentViewMode);
        } catch (error) {
            if (isCurrent) console.error(error);
        } finally {
            if (isCurrent) loading.value = false;
        }
    };

    watch([dateRange, viewMode, () => tzStore.effectiveTimezone], (_value, _oldValue, onCleanup) => {
        void fetchData(onCleanup);
    }, { immediate: true });

    return {
        viewMode,
        activeStatId,
        stats,
        chartData,
        chartOptions,
        openBucket,
        dailyAverage,
        loading,
        categoryData,
        rangeIncludesCurrentPeriod,
    };
}
